import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Heart } from "lucide-react";
import axios from "axios";
import BackgroundVideo from "@/components/BackgroundVideo";
import KineticText from "@/components/KineticText";
import SiteFooter from "@/components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { title: "30% OFF", detail: "your first launch order" },
  { title: "EXCLUSIVE MEMBER DISCOUNTS", detail: "reserved for our early community" },
  { title: "FIRST ACCESS", detail: "to the launch, new products & everything One Skin" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const EarlyAccess = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const digits = phone.replace(/\D/g, "");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("That email doesn't look right — mind checking it?");
      return;
    }
    if (digits.length < 10) {
      setError("Please enter your full 10-digit phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        phone: `+91 ${digits.slice(-10)}`,
      });
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.detail?.[0]?.msg ||
          err.response?.data?.detail ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid="early-access-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      <BackgroundVideo />
      <div className="grain-overlay" />

      <header className="relative z-10 flex items-center justify-between px-8 md:px-16 lg:px-24 pt-8 md:pt-10">
        <Link to="/" data-testid="back-to-manifesto-link">
          <img src="/assets/oneskin-logo.png" alt="One Skin" className="w-24 md:w-28" />
        </Link>
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#3D6B55] hover:text-[#1E3A2F] transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
      </header>

      <section className="relative z-10 px-8 md:px-16 lg:px-24 pt-14 md:pt-20 pb-8">
        <div className="max-w-4xl">
          <KineticText
            testIdPrefix="early-line"
            lines={[
              {
                text: "YOU'RE EARLY.",
                className:
                  "font-heading font-black text-[#1E3A2F] text-5xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight",
              },
              {
                text: (
                  <>
                    WE LOVE THAT.{" "}
                    <Heart
                      data-testid="love-heart-icon"
                      className="inline-block w-[0.55em] h-[0.55em] fill-[#8B1A2A] align-baseline"
                      strokeWidth={0}
                    />
                  </>
                ),
                className:
                  "font-heading font-black italic text-[#8B1A2A] text-5xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight",
              },
            ]}
          />
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#3D6B55] font-medium"
            data-testid="early-intro"
          >
            Get <strong className="text-[#1E3A2F]">30% OFF</strong> your first One Skin order.
            And because you found us before everyone else, you&#8217;re getting more than just
            launch-day savings.
          </motion.p>
        </div>
      </section>

      <section className="relative z-10 px-8 md:px-16 lg:px-24 py-10">
        <div className="max-w-4xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="text-xs tracking-[0.3em] uppercase text-[#3D6B55] mb-6"
          >
            Join the One Skin community and unlock
          </motion.p>
          {PERKS.map((perk, i) => (
            <motion.div
              key={perk.title}
              data-testid={`perk-${i}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className={`py-8 md:py-12 max-w-xl ${i % 2 === 1 ? "md:ml-auto md:text-right" : ""}`}
            >
              <h3 className="font-heading font-black text-[#8B1A2A] text-3xl md:text-5xl tracking-tight mb-3">
                {perk.title}
              </h3>
              <p className="text-base md:text-lg text-[#3D6B55] font-medium">{perk.detail}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 px-8 md:px-16 lg:px-24 py-14 md:py-20">
        <div className="max-w-4xl grid lg:grid-cols-2 gap-12 items-start">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="font-heading font-bold text-[#1E3A2F] text-3xl md:text-5xl leading-tight"
          >
            Get on <span className="italic text-[#8B1A2A]">the list.</span>
          </motion.h2>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="bg-[#FAF7F2]/90 backdrop-blur-sm border border-[#C8DDD1] p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(30,58,47,0.35)]"
            data-testid="waitlist-panel"
          >
            {result ? (
              <div data-testid="waitlist-success" className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-[#1E3A2F] flex items-center justify-center mx-auto mb-6">
                  <Check className="w-7 h-7 text-[#F5EFE6]" strokeWidth={3} />
                </div>
                <h3 className="font-heading font-bold text-[#1E3A2F] text-2xl md:text-3xl mb-3">
                  {result.already_registered ? "You're already in." : "You're on the list."}
                </h3>
                <p className="text-[#3D6B55] font-medium text-sm md:text-base leading-relaxed">
                  {result.already_registered
                    ? "Good things come to those who wait — and you beat us to it. Your 30% launch code is still yours."
                    : `Welcome to the early community — you're #${result.position} in line. Your 30% launch code lands in your inbox first.`}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="waitlist-form">
                <label
                  htmlFor="waitlist-email"
                  className="block text-xs tracking-[0.25em] uppercase text-[#3D6B55] mb-2 font-bold"
                >
                  Email address
                </label>
                <input
                  id="waitlist-email"
                  data-testid="waitlist-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="bg-transparent border-b-2 border-[#1E3A2F]/20 focus:border-[#1E3A2F] text-[#1E3A2F] py-3 outline-none transition-colors duration-300 w-full placeholder:text-[#3D6B55]/40 font-medium mb-7"
                />
                <label
                  htmlFor="waitlist-phone"
                  className="block text-xs tracking-[0.25em] uppercase text-[#3D6B55] mb-2 font-bold"
                >
                  Phone number
                </label>
                <div className="flex items-end gap-3 mb-8">
                  <span className="text-[#1E3A2F] font-bold py-3 border-b-2 border-[#1E3A2F]/20">
                    +91
                  </span>
                  <input
                    id="waitlist-phone"
                    data-testid="waitlist-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="XXXXX XXXXX"
                    className="bg-transparent border-b-2 border-[#1E3A2F]/20 focus:border-[#1E3A2F] text-[#1E3A2F] py-3 outline-none transition-colors duration-300 w-full placeholder:text-[#3D6B55]/40 font-medium"
                  />
                </div>
                {error && (
                  <p data-testid="waitlist-error" className="text-[#8B1A2A] text-sm font-medium mb-5">
                    {error}
                  </p>
                )}
                <motion.button
                  data-testid="count-me-in-btn"
                  type="submit"
                  disabled={loading}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="group w-full inline-flex items-center justify-center gap-3 bg-[#8B1A2A] text-[#F5EFE6] px-8 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#B5404A] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Adding you…" : "Count me in"}
                  {!loading && (
                    <ArrowRight
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                      strokeWidth={2.5}
                    />
                  )}
                </motion.button>
                <p className="text-xs text-[#3D6B55]/80 leading-relaxed mt-6 italic">
                  We&#8217;ll use your details to send your launch access, discount and occasional
                  One Skin updates. No spam. Just good skincare.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 px-8 md:px-16 lg:px-24 pb-20 md:pb-28">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="flex items-center justify-center gap-4 text-center"
        >
          <img
            src="/assets/oneskin-logo.png"
            alt="One Skin"
            data-testid="closing-logo"
            className="w-20 md:w-24"
          />
          <p className="font-heading text-[#1E3A2F] text-xl md:text-2xl">is almost here.</p>
        </motion.div>
      </section>

      <SiteFooter />
    </motion.div>
  );
};

export default EarlyAccess;
